<template>
  <div>
    <b-tabs
      card pills lazy
      :vertical="isWindowSM"
      v-model="currentTab"
      @activate-tab="tabChanged"
      nav-wrapper-class="bg-white"
      nav-class="bg-white"
    >
      <b-tab title="Prizes">
          <advanced-form
            autocomplete="off"
            layout="inline-md-3"
            submit-label="Save Leaderboard"
            v-bind="formFields"
            v-model="form"
            @submit="input()"
          >
              <template v-slot:prizes.first.title-before>
                <hr/>
                <b-form-group :label="form.raffleMode ? 'Leaderboard Prize 🏆' : 'Leaderboard First Prize 🥇'"/>
              </template>
              <template v-slot:prizes.second.title-before>
                <hr/>
                <b-form-group :label="form.raffleMode ? 'Leaderboard Prize 🏆' : 'Leaderboard Second Prize 🥈'"/>
              </template>
              <template v-slot:prizes.third.title-before>
                <hr/>
                <b-form-group :label="form.raffleMode ? 'Leaderboard Prize 🏆' : 'Leaderboard Third Prize 🥉'"/>
              </template>
          </advanced-form>
        </b-tab>
        <b-tab title="Score System">
          <div class="d-flex flex-column flex-fill">
            <b-card class="mb-4" v-for="(score, i) in scores" :key="`${i}-${score.id}`">
              <b-form-group label-cols="12" label-cols-md="3" label="Label (Required)" description="This name appears on the Leaderboard column header">
                <b-form-input v-model="score.label"/>
              </b-form-group>
              <b-form-group label-cols="12" label-cols-md="3" label="Value (Required)"
                :description="leaderboard.raffleMode ? 'In raffle mode, these are the number of tickets awarded'
                  : 'In score mode, these are the number of points awarded'">
                <b-form-input required type="number" @mousewheel="e => e.preventDefault()" v-model="score.value"/>
              </b-form-group>
              <b-form-group label-cols="12" label-cols-md="3" label="Description" description="This appears in the explainations of the score system">
                <b-form-input v-model="score.description"/>
              </b-form-group>
              <b-form-group label-cols="12" label-cols-md="3" label="Icon" description="This appears in the explainations of the score system">
                <b-form-radio v-for="icon in scoreIcons" :key="`${i}-${icon}`" :value="icon" v-model="score.icon" :name="`${score.id}-icon`">
                  <font-awesome-icon :icon="icon" />
                </b-form-radio>
              </b-form-group>
              <b-form-group label-cols="12" label-cols-md="3" label="Daily Cap" description="The maximum amount of points a user can collect a day.">
                <b-form-input type="number" v-model="score.dailyMaximum" />
              </b-form-group>
              <b-form-group label-cols="12" label-cols-md="3" label="Weekly Cap" description="The maximum amount of points a user can collect a week.">
                <b-form-input type="number" v-model="score.weeklyMaximum" />
              </b-form-group>
              <b-form-group label-cols="12" label-cols-md="3" label="Event Names">
                <checkbox-group :name="`${score.id}-scores`" :options="scoreEventNames" v-model="score.eventNames"/>
              </b-form-group>
              <b-form-group label-cols="12" label-cols-md="3" label="Disabled"
                description="Mark this as disabled to hide from the leaderboard and not collect any points for the events">
                  <b-form-checkbox v-model="score.disabled"/>
              </b-form-group>
              <div class="d-flex flex-row justify-content-end">
                <b-btn variant="outline-primary mr-2" @click="() => scoreMoveItem(true, i)">Up</b-btn>
                <b-btn variant="outline-primary mr-2" @click="() => scoreMoveItem(false, i)">Down</b-btn>
                <b-btn variant="outline-danger" @click="() => scores.splice(i, 1)">Remove</b-btn>
              </div>
            </b-card>
            <div class="d-flex flex-row justify-content-end">
              <b-btn variant="outline-primary" @click="() => scores.push({ id: Date.now(), eventNames: [], value: 1 })">Add New</b-btn>
            </div>
          </div>
          <div>
            <b-button variant="primary" @click="input()">Save Leaderboard</b-button>
          </div>
        </b-tab>
    </b-tabs>
  </div>
</template>

<script>
import AdvancedForm from '@src/components/AdvancedForm';
import CheckboxGroup from '@/components/settings/CheckboxGroup';
import Share from '@src/components/Share';
import _merge from 'lodash/merge';
import gridMixin from '@src/mixins/grid';

export default {
  props: {
    value: {
      type: Object,
    },
  },
  components: {
    AdvancedForm,
    Share,
    CheckboxGroup,
  },
  data: () => ({
    form: {},
    scores: [],
    leaderboard: {},
    currentTab: null,
  }),
  mixins: [
    gridMixin,
  ],
  computed: {
    formFields() {
      const form = {
        raffleMode: {
          label: 'Raffle Mode',
          type: 'CHECKBOX',
          description: 'Enable Raffle Mode 🎟',
          helpText: 'Raffle Mode allows participants to collect tickets to increase their chances of winning',
        },

        ...[ 'prizes.first', 'prizes.second', 'prizes.third' ].reduce((list, key) => ({
          ...list,
          [`${key}.title`]: {
            label: '— Title',
            type: 'STRING',
            helpText: 'Leave this empty to remove this prize',
            validation: {
              minLength: 3,
            },
          },
          [`${key}.image`]: {
            label: '— Image',
            type: 'FILE',
            helpText: 'Optionally include an image for the prize - for best results use a square image.',
            options: { type: 'IMAGE', accept: '.jpg,.jpeg,.gif,.png', showPreview: true },
          },
          [`${key}.description`]: {
            label: '— Description',
            type: 'TEXTAREA',
            helpText: 'Optionally write a description for this prize',
          },
          [`${key}.url`]: {
            label: '— URL',
            type: 'STRING',
            helpText: 'Optionally add a "Learn more" link to prizes',
            validation: {
              isURL: true,
            },
          },
          [`${key}.terms`]: {
            label: '— Terms',
            type: 'TEXTAREA',
            helpText: 'Optionally include any relevant Terms & Conditions',
          },
        }), {}),
      };

      return { form };
    },
    scoreIcons: () => ([
      'plug',
      'link',
      'file-alt',
      'users',
      'handshake',
    ]),
    scoreEventNames: () => ([
      {
        text: 'Job Shared',
        value: 'JOB_SHARED',
        tooltip: 'Awarded when a user shares a job (each time)',
      },
      {
        text: 'Referral Suggested',
        value: 'REFERRAL_SUGGESTED',
        tooltip: 'Awarded when a user chooses to refer a contact',
      },
      {
        text: 'Jam Referral Suggested',
        value: 'EVENT_REFERRAL_CREATED',
        tooltip: 'Awarded when a user chooses to refer a contact during a referral jam',
      },
      {
        text: 'LinkedIn Connections Browsed',
        value: 'JOB_BROWSE_LINKEDIN',
        tooltip: 'Awarded when a user searches for connections on LinkedIn (each time)',
      },
      {
        text: 'Job Scheduled',
        value: 'JOB_SCHEDULE_SHARE_FIRST',
        tooltip: 'Awarded when a user schedules a jobs for sharing',
      },
      {
        text: 'Job Viewed',
        value: 'SHARE_VISITED',
        tooltip: 'Awarded when a visitor views a share link (each time)',
      },
      {
        text: 'Job Viewed (Unique)',
        value: 'SHARE_UNIQUE_VISITED',
        tooltip: 'Awarded every time when a new visitor views a share link',
      },
      {
        text: 'Job Viewed at the First Time',
        value: 'SHARE_FIRST_VISITED',
        tooltip: 'Awarded when first visitor views a share link',
      },
      {
        text: 'Content Shared',
        value: 'CONTENT_SHARED',
        tooltip: 'Awarded when a user shares a content link (each time)',
      },
      {
        text: 'Content Scheduled',
        value: 'CONTENT_SCHEDULE_SHARE_FIRST',
        tooltip: 'Awarded when a user schedules a piece of content for sharing',
      },
      {
        text: 'Content Viewed',
        value: 'CONTENT_SHARE_VISITED',
        tooltip: 'Awarded when a visitor views content (each time)',
      },
      {
        text: 'Content Viewed (Unique)',
        value: 'CONTENT_SHARE_UNIQUE_VISITED',
        tooltip: 'Awarded every time when a new visitor views content',
      },
      {
        text: 'Content Viewed at the First Time',
        value: 'CONTENT_SHARE_FIRST_VISITED',
        tooltip: 'Awarded when the piece of content is visited at the first time',
      },
      {
        text: 'Application Created',
        value: 'APPLICATION_CREATED',
        tooltip: 'Awarded when a user\'s candidate applies for a role',
      },
      {
        text: 'Application Interviewed',
        value: 'APPLICATION_INTERVIEW',
        tooltip: 'Awarded when a user\'s candidate is moved to Interview',
      },
      {
        text: 'Application Hired',
        value: 'APPLICATION_HIRED',
        tooltip: 'Awarded when a user\'s candidate is moved to Hired',
      },
    ]),
  },
  created() {
    const { leaderboard } = this.value;
    console.log({ leaderboard });
    this.leaderboard = { ...(leaderboard || {}) };
    this.scores = JSON.parse(JSON.stringify([ ...(Array.isArray(leaderboard.scores) ? leaderboard.scores : []) ])).map(el => {
      delete el.__typename;
      return el;
    });
    Object.keys(this.formFields.form).forEach(key => {
      if (key.includes('.')) {
        this.form[key] = key.split('.').reduce((out, k, i) => {
          if (i === 0) {
            return leaderboard ? leaderboard[k] : null;
          }
          return out ? out[k] : null;
        }, null);
      } else {
        this.form[key] = leaderboard[key];
      }
    });
  },
  methods: {
    input() {
      console.log({ keys: Object.keys(this.form) });
      const component = { leaderboard: Object.keys(this.form).reduce((out, key) => {
        if (key.includes('.')) {
          const subKeys = key.split('.').reverse();
          const subTree = subKeys.reduce((o, k, i) => {
            if (i === 0) {
              if (k === 'image') {
                o[k] = this.form[key] ? { id: this.form[key].id } : null;
              } else {
                o[k] = this.form[key];
              }
              return o;
            }
            o[k] = JSON.parse(JSON.stringify(o));
            delete o[subKeys[i - 1]];
            return o;
          }, {});
          return _merge(out, subTree);
        }
        if (key === 'scores') {
          console.log('Overwrite scores: ', [ ...(Array.isArray(this.scores) ? this.scores : []) ]);
          out[key] = [ ...(Array.isArray(this.scores) ? this.scores : []) ];
          return out;
        }
        out[key] = this.form[key];
        return out;
      }, {}) };
      component.leaderboard.scores = [ ...(Array.isArray(this.scores) ? this.scores : []) ].map(el => {
        el.value = parseInt(el.value);
        el.dailyMaximum = parseInt(el.dailyMaximum);
        el.weeklyMaximum = parseInt(el.weeklyMaximum);
        return el;
      });
      console.log(JSON.parse(JSON.stringify({ component })));
      this.$emit('input', component);
    },
    tabChanged(next, prev, e) {
      console.log({ next, prev, e });
      return true;
    },
    scoreMoveItem(up, i) {
      if (i === 0 && up) {
        return false;
      }
      if (i === this.scores.length - 1 && !up) {
        return false;
      }

      const j = up ? i - 1 : i + 1;
      const [ value ] = this.scores.splice(i, 1);
      this.scores.splice(j, 0, value);
    },
  }
};
</script>

<style lang="scss">
.referral-jam-event-settings {
  .event-logo {
    max-height: 30px;
    max-width: 200px;
  }
}
</style>



// WEBPACK FOOTER //
// src/components/campaign-admin/update-leaderboard.vue